// assets/js/podcast-player.js
// Player podcast auto-hébergé (Shikwasa) : yarn add shikwasa  (le projet est verrouillé sur Yarn 1, ne pas utiliser npm install)
//
// Importé par l'entrée Encore (app.js) : import './js/podcast-player';
//
// Page /podcasts (full/podcast_listing.html.twig) :
//   - .podcast-featured contient toutes les fiches (vue line/podcast.html.twig), une seule est
//     visible (.is-active) ; chaque fiche a son lecteur Shikwasa (.podcast-player).
//   - .podcast-playlist liste les épisodes (line/podcast_row.html.twig) : cliquer une ligne
//     affiche la fiche du même contenu et lance la lecture.
//
// ⚠️ Si la page podcasts passe un jour au rechargement AJAX (bloc isXmlHttpRequest du
// PodcastController), rappeler les trois init* sur le conteneur injecté :
//   container.innerHTML = json.html;
//   initPodcastPlayers(container); initPodcastPlaylist(container); initPodcastDescriptions(container);

import 'shikwasa/dist/style.css';
import { Player } from 'shikwasa';

// Élément .podcast-player -> instance Shikwasa
const players = new Map();

export function getPodcastPlayer(el) {
    return players.get(el);
}

export function initPodcastPlayers(root = document) {
    root.querySelectorAll('.podcast-player:not([data-player-init])').forEach((el) => {
        // Garde-fou : évite la double initialisation du même élément
        el.dataset.playerInit = '1';

        const player = new Player({
            container: () => el,
            themeColor: '#bf2923', // couleur de marque (cf. _podcasts.css)
            // 'static' : le player reste dans le flux de la page. Le défaut 'auto'
            // le fixe en bas du viewport sur petit écran, et dans le listing
            // podcasts plusieurs players se superposeraient.
            fixed: { type: 'static' },
            audio: {
                title: el.dataset.title,
                cover: el.dataset.cover || null,
                src: el.dataset.src,
            },
        });
        players.set(el, player);

        // Un seul épisode à la fois : lancer une lecture met les autres en pause
        player.audio.addEventListener('play', () => {
            players.forEach((other) => {
                if (other !== player && !other.audio.paused) other.pause();
            });
        });

        replaceSeekIcons(el);
        addVolumeSlider(el, player);
    });
}

// Icônes -10s / +10s : dans celles de Shikwasa, le "10" et la flèche forment un seul
// tracé, et à 20px les chiffres touchent la flèche. On garde la flèche circulaire
// d'origine (premier sous-tracé du symbole) et on redessine le "10" en texte, un peu plus
// petit (58 unités sur 128, contre ~80 dans l'icône d'origine).
const SEEK_ARROWS = {
    backward: 'M64 0v11A64 64 0 1 1 0 75h14a50 50 0 1 0 50-50v13L16 19 64 0z',
    forward: 'M64 11v14h-.8A50 50 0 1 0 114 75h14a64 64 0 1 1-64-64zM64 0l48 19-48 19V0z',
};

function replaceSeekIcons(el) {
    Object.entries(SEEK_ARROWS).forEach(([key, d]) => {
        const svg = el.querySelector(`.shk-btn_${key} svg`);
        if (!svg) return;
        svg.setAttribute('viewBox', '0 0 128 139');
        svg.innerHTML = `<path fill="currentColor" d="${d}"/>`
            + '<text x="64" y="96" text-anchor="middle" font-size="58" font-weight="700" fill="currentColor">10</text>';
    });
}

// Curseur de volume : Shikwasa n'a qu'un bouton muet. On insère un <input type="range">
// après ce bouton, relié au volume de l'élément audio du lecteur. Le bouton muet et le
// curseur restent synchronisés via l'événement volumechange. Masqué sur mobile par le
// CSS (iOS ignore le volume logiciel).
function addVolumeSlider(el, player) {
    const volumeBtn = el.querySelector('.shk-btn_volume');
    if (!volumeBtn || !player.audio) return;

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '1';
    slider.step = '0.05';
    slider.className = 'podcast-volume';
    slider.setAttribute('aria-label', 'Volume');

    const render = () => {
        const v = player.muted ? 0 : player.audio.volume;
        slider.value = String(v);
        slider.style.setProperty('--podcast-volume', `${Math.round(v * 100)}%`);
    };

    slider.addEventListener('input', () => {
        const v = parseFloat(slider.value);
        player.audio.volume = v;
        if (v > 0 && player.muted) player.muted = false;
        if (v === 0 && !player.muted) player.muted = true;
        render();
    });
    player.audio.addEventListener('volumechange', render);

    volumeBtn.insertAdjacentElement('afterend', slider);
    render();
}

// Description repliée sur 3 lignes (CSS) : quand le texte tient dans la boîte,
// on masque le lien "Voir plus" en posant .is-short. Mesuré après le chargement
// des polices, sinon le nombre de lignes peut encore changer. Les fiches masquées
// (display:none) ne sont pas mesurables : elles le seront à leur affichage.
export function initPodcastDescriptions(root = document) {
    root.querySelectorAll('.podcast-description:not([data-description-init])').forEach((el) => {
        if (el.offsetParent === null) return;
        el.dataset.descriptionInit = '1';
        const text = el.querySelector('.podcast-description-text');
        if (text && text.scrollHeight <= text.clientHeight + 1) {
            el.classList.add('is-short');
        }
    });
}

// Liste des épisodes (playlist) : activation de la fiche, description dépliable,
// état de lecture et durée sur chaque ligne.
export function initPodcastPlaylist(root = document) {
    root.querySelectorAll('.podcast-playlist:not([data-playlist-init])').forEach((list) => {
        list.dataset.playlistInit = '1';

        const section = list.closest('.podcast-listing') || root;
        const featured = section.querySelector('.podcast-featured');
        if (!featured) return;

        const cards = Array.from(featured.querySelectorAll('.podcast-article'));
        const rows = Array.from(list.querySelectorAll('.podcast-row'));
        const cardFor = (id) => cards.find((card) => card.dataset.contentId === id);
        const playerFor = (id) => {
            const card = cardFor(id);
            return card ? players.get(card.querySelector('.podcast-player')) : undefined;
        };

        const activate = (id, { play = false, scroll = false } = {}) => {
            const card = cardFor(id);
            if (!card) return;
            cards.forEach((c) => c.classList.toggle('is-active', c === card));
            rows.forEach((r) => r.classList.toggle('is-active', r.dataset.contentId === id));

            if (scroll) {
                // Marge pour le header fixe du site
                const top = featured.getBoundingClientRect().top + window.scrollY - 120;
                window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
                // La fiche vient d'apparaître : sa description devient mesurable
                card.querySelectorAll('.podcast-description').forEach((d) => {
                    delete d.dataset.descriptionInit;
                    d.classList.remove('is-short');
                });
                initPodcastDescriptions(card);
            }
            if (play) {
                const player = playerFor(id);
                if (player) player.play();
            }
        };

        // Fiche active au chargement : celle marquée côté serveur, sinon la première
        const initial = rows.find((r) => r.classList.contains('is-active')) || rows[0];
        if (initial) activate(initial.dataset.contentId);

        rows.forEach((row) => {
            const id = row.dataset.contentId;

            const select = row.querySelector('.podcast-row-select');
            if (select) select.addEventListener('click', () => activate(id, { play: true, scroll: true }));

            // Description dépliable (bouton "i" / "Voir moins")
            const info = row.querySelector('.podcast-row-info');
            const description = row.querySelector('.podcast-row-description');
            if (info && description) {
                const toggle = (open) => {
                    description.hidden = !open;
                    info.setAttribute('aria-expanded', String(open));
                    row.classList.toggle('is-open', open);
                };
                info.addEventListener('click', () => toggle(description.hidden));
                const less = description.querySelector('.podcast-row-less');
                if (less) less.addEventListener('click', () => toggle(false));
            }

            // État de lecture et durée : lus sur le lecteur de la fiche correspondante,
            // dont Shikwasa précharge déjà les métadonnées (pas de requête supplémentaire)
            const player = playerFor(id);
            const duration = row.querySelector('.podcast-row-duration');
            if (player) {
                const audio = player.audio;
                audio.addEventListener('play', () => row.classList.add('is-playing'));
                audio.addEventListener('pause', () => row.classList.remove('is-playing'));
                audio.addEventListener('ended', () => row.classList.remove('is-playing'));
                if (duration) {
                    const renderDuration = () => { duration.textContent = formatDuration(audio.duration); };
                    audio.addEventListener('loadedmetadata', renderDuration);
                    audio.addEventListener('durationchange', renderDuration);
                    renderDuration();
                }
            }
        });
    });
}

// 3725 s -> "1:02:05", 785 s -> "13:05"
function formatDuration(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) return '';
    const total = Math.round(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = h ? String(m).padStart(2, '0') : String(m);
    return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

document.addEventListener('DOMContentLoaded', () => {
    initPodcastPlayers();
    initPodcastPlaylist();
    const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
    fontsReady.then(() => initPodcastDescriptions());
});
